import React, { useMemo } from "react";
import LayoutDashboard from "layouts/layout-dashboard-collapse"; // Assuming this is a layout component
// import { useAppV3 } from "contexts/v3/BookingContextV3";
import { formatPrice } from "utils/utils";
import PrintJson from "utils/printJson";
// import { useKomisiBooking } from "contexts/KomisiBookingContext";
import { Card, Row, Col, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useAccounting } from "./context/AccountingContext";
import PaymentMethodAccordionAction from "./PaymentMethodAccordionAction";
import AccountingHeaderFilter from "./AccountingHeaderFilter";
import { getDevelopmentBanner } from "utils/devNotice";
import DompetAkunting from "./DompetAkunting";
import AccountingResumeCard from "./AccountingResumeCard";
import { useNavigate } from "react-router-dom";
import FormulaCalculator from "./FormulaCalculator";

const AccountingPage = () => {
  // const {
  //   bookings,
  //   dataPerPaymentMethod,
  //   filterOptions,
  //   updateFilter,
  //   updateSortOrder,
  // } = useAppV3();

  const {
    bookings,
    dataPerPaymentMethod,
    filterOptions,
    updateFilter,
    refetch,
    total,
    handleSearch,
    allPaymentMethods,
  } = useAccounting();

  const navigate = useNavigate();

  // filterOptions["filters[status]"] = "finished,played";

  // const paymentMethods = useMemo(() => {
  //   if (!bookings?.data) return [];
  //   const uniqueMethods = new Set();
  //   bookings.data.forEach((booking) => {
  //     if (booking.payment_method_id && booking.payment_method_name) {
  //       uniqueMethods.add(
  //         JSON.stringify({
  //           id: booking.payment_method_id,
  //           name: booking.payment_method_name,
  //         })
  //       );
  //     }
  //   });
  //   return Array.from(uniqueMethods).map((method) => JSON.parse(method));
  // }, [bookings]);

  const calculateNetKuber = React.useCallback((booking) => {
    // Use booking.type as the role (can be null)
    const effectiveRole = booking.type;

    // Use total_atv as TOTAL_MOTOR
    const totalMotor = Number(booking.total_atv) || 0;

    let deduction = 0;

    switch ((effectiveRole || "").toLowerCase()) {
      case "nam":
        deduction = totalMotor * 50000 + totalMotor * 50000; // 100000 per motor
        break;
      case "mm":
      case "bm":
        deduction = totalMotor * 50000 + totalMotor * 10000; // 60000 per motor
        break;
      case "j":
      case "josh":
      case "pj":
        deduction = totalMotor * 50000 + totalMotor * 25000; // 75000 per motor
        break;
      default:
        deduction = 0;
    }

    const netAgent = Number(booking.net_agent) || 0;
    const result = netAgent - deduction;

    // Ensure we return a valid number, not NaN
    return isNaN(result) ? 0 : result;
  }, []);

  // Calculate grand totals across all payment methods
  const grandTotals = useMemo(() => {
    if (!dataPerPaymentMethod || Object.keys(dataPerPaymentMethod).length === 0)
      return {
        totalPrice: 0,
        netPrice: 0,
        susukAgent: 0,
        others: 0,
        netKuber: 0,
        count: 0,
      };

    return Object.values(dataPerPaymentMethod).reduce(
      (acc, paymentData) => {
        // Safe number conversion for totals
        acc.totalPrice += Number(paymentData.totals.totalPrice) || 0;
        acc.netPrice += Number(paymentData.totals.netPrice) || 0;
        acc.susukAgent += Number(paymentData.totals.susukAgent) || 0;
        acc.others += Number(paymentData.totals.others) || 0;
        acc.count += Number(paymentData.totals.count) || 0;

        // Calculate net kuber using booking.type as primary role
        const netKuberForPaymentMethod = paymentData.data.reduce(
          (sum, booking, index) => {
            const netKuber = calculateNetKuber(booking);
            return sum + (Number(netKuber) || 0);
          },
          0
        );

        acc.netKuber += netKuberForPaymentMethod;

        return acc;
      },
      {
        totalPrice: 0,
        netPrice: 0,
        susukAgent: 0,
        others: 0,
        netKuber: 0,
        count: 0,
      }
    );
  }, [dataPerPaymentMethod, calculateNetKuber]);

  const getTextColor = (backgroundColor) => {
    const hex = backgroundColor.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    return luminance < 128 ? "#fff" : "#000";
  };

  // Calculate totals per type (nam, mm, bm, josh)
  const totalsPerType = useMemo(() => {
    const types = {
      nam: {
        count: 0,
        payment_bill: 0,
        net_agent: 0,
        net_kuber: 0,
        deduction: 0,
        others: 0,
      },
      mm: {
        count: 0,
        payment_bill: 0,
        net_agent: 0,
        net_kuber: 0,
        deduction: 0,
        others: 0,
      },
      bm: {
        count: 0,
        payment_bill: 0,
        net_agent: 0,
        net_kuber: 0,
        deduction: 0,
        others: 0,
      },
      josh: {
        count: 0,
        payment_bill: 0,
        net_agent: 0,
        net_kuber: 0,
        deduction: 0,
        others: 0,
      },
      pj: {
        count: 0,
        payment_bill: 0,
        net_agent: 0,
        net_kuber: 0,
        deduction: 0,
        others: 0,
      },
    };

    if (!dataPerPaymentMethod || Object.keys(dataPerPaymentMethod).length === 0)
      return types;

    Object.values(dataPerPaymentMethod).forEach((paymentData) => {
      paymentData.data.forEach((booking) => {
        const type = (booking.type || "").toLowerCase();
        let mappedType = null;
        if (type === "nam") mappedType = "nam";
        else if (type === "mm") mappedType = "mm";
        else if (type === "bm") mappedType = "bm";
        else if (type === "josh" || type === "j") mappedType = "josh";
        else if (type === "pj") mappedType = "pj";

        if (mappedType) {
          const totalMotor = Number(booking.total_atv) || 0;
          let deduction = 0;
          switch (type) {
            case "nam":
              deduction = totalMotor * 50000 + totalMotor * 50000;
              break;
            case "mm":
            case "bm":
              deduction = totalMotor * 50000 + totalMotor * 10000;
              break;
            case "j":
            case "josh":
            case "pj":
              deduction = totalMotor * 50000 + totalMotor * 25000;
              break;
            default:
              deduction = 0;
          }

          types[mappedType].count += 1;
          types[mappedType].payment_bill += Number(booking.payment_bill) || 0;
          types[mappedType].net_agent += Number(booking.net_agent) || 0;
          types[mappedType].net_kuber += calculateNetKuber(booking);
          
          const othersValue = totalMotor * 50000;
          types[mappedType].others += othersValue;

          // For BM, MM, J/Josh, PJ, the deduction in card should be net of others
          if (["mm", "bm", "josh", "pj"].includes(mappedType)) {
            types[mappedType].deduction += (deduction - othersValue);
          } else {
            types[mappedType].deduction += deduction;
          }
        }
      });
    });

    return types;
  }, [dataPerPaymentMethod, calculateNetKuber]);

  return (
    <LayoutDashboard>
      {getDevelopmentBanner("Halaman ini dalam pengembangan.")}{" "}
      {/* Panggil di JSX */}
      {/* <DevBanner message="Halaman ini dalam pengembangan." /> */}
      <div className="container-fluid mt-3">
        <AccountingHeaderFilter
          filterOptions={filterOptions}
          handleSearch={handleSearch}
          updateFilter={updateFilter}
          allPaymentMethods={allPaymentMethods}
        />

        {/* Summary Cards */}
        {/* <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h5 className="text-primary">{grandTotals.count}</h5>
                <small className="text-muted">Total Transactions</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h5 className="text-success">
                  {formatPrice(grandTotals.totalPrice)}
                </h5>
                <small className="text-muted">Total Revenue</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h5 className="text-info">
                  {formatPrice(grandTotals.netPrice)}
                </h5>
                <small className="text-muted">Net Agent</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h5 className="text-warning">
                  {formatPrice(grandTotals.netKuber)}
                </h5>
                <small className="text-muted">Net Kuber</small>
              </Card.Body>
            </Card>
          </Col>
        </Row> */}

        {/* Payment Method Accordion */}

        <PaymentMethodAccordionAction
          dataPerPaymentMethod={dataPerPaymentMethod}
          calculateNetKuber={calculateNetKuber}
          getTextColor={getTextColor}
          onRefreshData={refetch}
          allPaymentMethods={allPaymentMethods}
        />

        <h1 className="h4 mb-3">DOMPET DETAILS PER PAYMENT METHOD</h1>
        <DompetAkunting
          dataPerPaymentMethod={dataPerPaymentMethod}
          calculateNetKuber={calculateNetKuber}
          getTextColor={getTextColor}
          onRefreshData={refetch}
        />
        {/* <h1 className="h4 mb-3">DETAILS PER PAYMENT METHOD (No Action)</h1>
        <PaymentMethodAccordion
          dataPerPaymentMethod={dataPerPaymentMethod}
          roles={roles}
          handleRoleChange={handleRoleChange}
          calculateNetKuber={calculateNetKuber}
          getTextColor={getTextColor}
        /> */}

        {/* buat card untuk menampilkan total tiap tipe mm, pj, nm */}
        <h1 className="h4 mb-3">
          TOTAL PER TIPE (NAM, MM, BM, JOSH)
          {/* button other */}
          <Button
            variant="outline"
            className="ms-3"
            onClick={() => navigate("/v1/agents")}
          >
            Lihat Semua
          </Button>
        </h1>
        <Row className="mb-4 justify-content-center">
          {Object.entries(totalsPerType).map(([type, totals]) => (
            <Col md={3} key={type}>
              <Card className="text-center" style={{ minHeight: "180px" }}>
                <Card.Header className="text-center bg-primary text-white">
                  <h2 className="m-0">{type.toUpperCase()}</h2>
                </Card.Header>
                <Card.Body>
                  <p>
                    <OverlayTrigger placement="top" overlay={<Tooltip>Total transaksi untuk tipe {type.toUpperCase()}</Tooltip>}>
                      <strong style={{ cursor: "help", borderBottom: "1px dotted #000" }}>Transactions:</strong>
                    </OverlayTrigger> {totals.count}
                  </p>
                  <p>
                    <OverlayTrigger placement="top" overlay={<Tooltip>Perhitungan: Total Motor × Rp 50.000</Tooltip>}>
                      <strong style={{ cursor: "help", borderBottom: "1px dotted #000" }}>Other:</strong>
                    </OverlayTrigger> {formatPrice(totals.others)}
                  </p>
                  <p>
                    <OverlayTrigger placement="top" overlay={
                      <Tooltip>
                        {type === "nam" && "Perhitungan: (Motor × 50.000) + (Motor × 50.000)"}
                        {(type === "mm" || type === "bm") && "Perhitungan: (Motor × 50.000) + (Motor × 10.000) dikurangi Other"}
                        {(type === "josh" || type === "pj") && "Perhitungan: (Motor × 50.000) + (Motor × 25.000) dikurangi Other"}
                      </Tooltip>
                    }>
                      <strong style={{ cursor: "help", borderBottom: "1px dotted #000" }}>Total Deduction:</strong>
                    </OverlayTrigger>{" "}
                    {formatPrice(totals.deduction)}
                  </p>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Grand Total Summary */}
        <Card className="mt-4">
          <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0">GRAND TOTAL SUMMARY</h5>
          </Card.Header>
          <Card.Body>
            <FormulaCalculator />
            <Row>
              <Col md={2}>
                <OverlayTrigger placement="top" overlay={<Tooltip>Total seluruh transaksi di semua metode pembayaran</Tooltip>}>
                  <strong style={{ cursor: "help", borderBottom: "1px dotted #000" }}>Total Transactions:</strong>
                </OverlayTrigger>
                <br />
                <span className="h5 text-primary">{grandTotals.count}</span>
              </Col>
              <Col md={2}>
                <OverlayTrigger placement="top" overlay={<Tooltip>Total keseluruhan tagihan pelanggan (Payment Bill)</Tooltip>}>
                  <strong style={{ cursor: "help", borderBottom: "1px dotted #000" }}>Total Payment Bill:</strong>
                </OverlayTrigger>
                <br />
                <span className="h5 text-success">
                  {/* {formatPrice(grandTotals.totalPrice)} */}
                  {formatPrice(total.payment_bill)}
                </span>
              </Col>
              <Col md={2}>
                <OverlayTrigger placement="top" overlay={<Tooltip>Total Net Agent (Harga net yang diberikan dari agen)</Tooltip>}>
                  <strong style={{ cursor: "help", borderBottom: "1px dotted #000" }}>Net Agent:</strong>
                </OverlayTrigger>
                <br />
                <span className="h5 text-info">
                  {/* {formatPrice(grandTotals.netPrice)} */}
                  {formatPrice(total.net_agent)}
                </span>
              </Col>
              <Col md={2}>
                <OverlayTrigger placement="top" overlay={<Tooltip>Susuk / kembalian / profit tambahan agen (Payment Bill dikurangi Net Agent)</Tooltip>}>
                  <strong style={{ cursor: "help", borderBottom: "1px dotted #000" }}>Susuk Agent:</strong>
                </OverlayTrigger>
                <br />
                <span className="h5 text-secondary">
                  {/* {formatPrice(grandTotals.susukAgent)} */}
                  {formatPrice(total.susuk_agent)}
                </span>
              </Col>
              <Col md={2}>
                <OverlayTrigger placement="top" overlay={<Tooltip>Total biaya lainnya (Total Motor × 50.000)</Tooltip>}>
                  <strong style={{ cursor: "help", borderBottom: "1px dotted #000" }}>Others:</strong>
                </OverlayTrigger>
                <br />
                <span className="h5 text-warning">
                  {/* {formatPrice(grandTotals.others)} */}
                  {formatPrice(total.others)}
                </span>
              </Col>
              <Col md={2}>
                <OverlayTrigger placement="top" overlay={<Tooltip>Pendapatan Bersih Kuber (Net Agent dikurangi Total Deduction)</Tooltip>}>
                  <strong style={{ cursor: "help", borderBottom: "1px dotted #000" }}>Net Kuber:</strong>
                </OverlayTrigger>
                <br />
                <span className="h5 text-success">
                  {/* {formatPrice(grandTotals.netKuber)} */}
                  {formatPrice(total.net_kuber)}
                </span>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* card baru untuk resume buat komponen baru */}
        <AccountingResumeCard 
          total={total}
          totalsPerType={totalsPerType}
        />

        {/* Debug JSON - Remove in production */}
        <PrintJson data={total} />
        <PrintJson data={bookings} />
        {/* {
  "payment_bill": "118920000.00",
  "net_agent": "74800000.00",
  "susuk_agent": "44120000.00",
  "net_kuber": "65600000.00",
  "others": "9000000.00",
  "net_agent_final": "66000000.00"
} */}
      </div>
    </LayoutDashboard>
  );
};

export default AccountingPage;
